import React, { Dispatch, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import { cancel_button, popup_padding, save_button } from 'src/config/config'
import { useEditPortfolioTagMutation } from 'src/graphql/manual/portfolioCustomTagHooks'
import { showToast } from 'src/store/actions/toastAction'

import styles from './EditTagModal.module.css'

interface Props {
  isOpen: boolean
  tagId: string
  tagName: string
  sharedPortfolioId?: string
  onClose: () => void
  onSuccess: () => void
}

export const EditTagModal: React.FC<Props> = ({
  isOpen,
  tagId,
  tagName,
  sharedPortfolioId = '',
  onClose,
  onSuccess,
}) => {
  const [value, setValue] = useState(tagName)
  const inputRef = useRef<HTMLInputElement>(null)
  const [editPortfolioTag, { loading }] = useEditPortfolioTagMutation()
  const dispatch: Dispatch<any> = useDispatch()

  useEffect(() => {
    if (isOpen) {
      setValue(tagName)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, tagName])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const handleSave = async () => {
    if (!value.trim() || loading) return

    if (value.trim().length > 50) {
      dispatch(showToast('Custom tag name cannot exceed 50 characters.', 'info'))
      return
    }

    try {
      const result = await editPortfolioTag({
        variables: {
          tagDocumentId: tagId,
          name: value.trim(),
          sharedPortfolioId: sharedPortfolioId || null,
        },
      })
      onSuccess()
      onClose()
      if (result.data?.editPortfolioTag?.id) {
        dispatch(showToast('Custom tag updated successfully', 'success'))
      }
    } catch (err: unknown) {
      dispatch(showToast(err instanceof Error ? err.message : 'Failed to edit tag', 'info'))
    }
  }

  if (!isOpen) return null

  const disabled = !value.trim() || loading

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative md:mx-0 mx-4 w-full md:w-[600px]   rounded-[8px] bg-white shadow-[-16px_17px_37px_0px_rgba(0,0,0,0.1)] ${popup_padding}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="edit-tag-title"
        aria-modal="true"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-[-10px] right-[-9px] z-20 flex h-6 w-6 items-center justify-center rounded-full bg-[#011D58] text-white hover:opacity-90"
          aria-label="Close"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1 1L9 9M9 1L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div
          className={`relative w-full rounded-[8px] bg-white`}
          /* style={{ minHeight: 416 }} */
          role="dialog"
          aria-labelledby="add-custom-tag-title"
          aria-modal="true"
        >
          <h2
            id="edit-tag-title"
            className="font-secondary text-[24px] font-bold leading-[1.5] text-[#333333] md:mb-[10px] mb-[8px]"
          >
            Edit Custom Tag
          </h2>

          <div className="h-px w-full bg-[#DDDDDD]" />
          <p className="text-[16px] font-secondary text-darkgray mt-6">
            Update the custom tag name below. Changes will automatically apply to all solutions associated with this
            tag.
          </p>

          <div className="mt-6 flex h-[40px] w-full items-center overflow-hidden rounded-[4px] border border-[#DDDDDD] bg-white">
            <input
              id="edit-tag-input"
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSave()
              }}
              className="h-full w-full bg-transparent px-[12px] font-primary text-[15px] font-medium text-[#011D58] outline-none"
              autoComplete="off"
            />
          </div>

          <div className="mt-6 flex w-full min-w-0 flex-row flex-wrap items-center md:gap-[16px] gap-[8px]">
            <button
              className={`${styles.saveButton} ${save_button}`}
              type="button"
              onClick={() => void handleSave()}
              disabled={disabled}
            >
              <p className="text-white">{loading ? 'Save' : 'Save'}</p>
            </button>

            <button className={`${cancel_button}`} type="button" onClick={onClose}>
              <p className="text-[#333333]">Cancel</p>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
